import { useAutoneTranslation } from '@autone/translations';
import { type PropsWithChildren, useMemo } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../atoms';
import { cn } from '../../../utils';
import { BadgeListWithOverflow } from '../../BadgeListWithOverflow';

import {
  ClearAllButton,
  GeneralFiltersGroupOptions,
  GeneralFiltersNoOptions,
} from './GeneralFilters.components';
import {
  GeneralFiltersContext,
  useGeneralFiltersContext,
} from './GeneralFilters.context';
import {
  filterGroupsWithOptions,
  partitionFilterGroups,
} from './GeneralFilters.helpers';
import { type GeneralFilterGroup, type SelectedGeneralFilters } from './types';
import {
  groupGeneralFilterGroups,
  LOCATION_FILTER_ORDER,
  PRODUCT_FILTER_ORDER,
} from './utils/groupGeneralFilterGroups';

// --------------------
// Base Provider Component
// --------------------
export interface GeneralFiltersProps extends PropsWithChildren {
  filterGroups: GeneralFilterGroup[];
  selectedFilters: SelectedGeneralFilters;
  onSelectedFiltersChange: (selectedFilters: SelectedGeneralFilters) => void;
}

export const GeneralFiltersContextProvider = ({
  children,
  filterGroups,
  selectedFilters,
  onSelectedFiltersChange,
}: GeneralFiltersProps) => {
  const toggleOption = (groupId: string, optionId: string, value: boolean) => {
    const newSelectedFilters = {
      ...selectedFilters,
      [groupId]: {
        ...selectedFilters[groupId],
        [optionId]: value,
      },
    };
    onSelectedFiltersChange(newSelectedFilters);
  };

  const toggleFilterGroup = (
    groupId: string,
    value: boolean,
    optionIds?: string[],
  ) => {
    const filterGroup = filterGroups.find((group) => group.id === groupId);
    if (filterGroup) {
      const targetOptionIds =
        optionIds ?? filterGroup.options.map((option) => option.id);
      const newSelectedFilters = {
        ...selectedFilters,
        [groupId]: {
          ...selectedFilters[groupId],
          ...Object.fromEntries(
            targetOptionIds.map((optionId) => [optionId, value]),
          ),
        },
      };
      onSelectedFiltersChange(newSelectedFilters);
    }
  };

  const sortedFilterGroups = useMemo(() => {
    return groupGeneralFilterGroups(filterGroups).map((group) => ({
      ...group,
      options: [...group.options].sort((a, b) =>
        a.translatedDisplayName.localeCompare(b.translatedDisplayName),
      ),
    }));
  }, [filterGroups]);

  return (
    <GeneralFiltersContext.Provider
      value={{
        filterGroups: sortedFilterGroups,
        toggleOption,
        toggleFilterGroup,
        setToggledOptions: onSelectedFiltersChange,
        toggledOptions: selectedFilters,
      }}
    >
      {children}
    </GeneralFiltersContext.Provider>
  );
};

// --------------------
// Accordion Root
// --------------------

const Root = ({ children }: PropsWithChildren) => (
  <Accordion type="multiple" className="flex flex-col w-full bg-background">
    {children}
  </Accordion>
);

// --------------------
// Accordion Item (Filter Group)
// --------------------
interface ItemProps extends PropsWithChildren {
  value: string;
  className?: string;
}

const Item = ({ value, children, className }: ItemProps) => (
  <AccordionItem
    value={value}
    className={cn('border-b border-muted', className)}
  >
    {children}
  </AccordionItem>
);

// --------------------
// Trigger Header
// --------------------
interface TriggerProps extends PropsWithChildren {
  className?: string;
}

const Trigger = ({ children, className }: TriggerProps) => (
  <AccordionTrigger
    id="general-filters-trigger"
    aria-label="General filters trigger"
    className={cn(
      'flex items-center justify-between px-3 py-2 gap-0.5 hover:bg-secondary text-sm font-medium',
      className,
    )}
  >
    {children}
  </AccordionTrigger>
);

// --------------------
// Content Panel
// --------------------
interface ContentProps extends PropsWithChildren {
  className?: string;
}

const Content = ({ children, className }: ContentProps) => (
  <AccordionContent className={cn('px-0 overflow-y-scroll', className)}>
    {children}
  </AccordionContent>
);

export const GeneralFilters = Object.assign(GeneralFiltersContextProvider, {
  Root,
  Item,
  Trigger,
  Content,
});

interface GeneralFiltersSectionProps {
  title: string;
  groups: GeneralFilterGroup[];
  toggledOptions: SelectedGeneralFilters;
  titleClassName?: string;
}

const GeneralFiltersSection = ({
  title,
  groups,
  toggledOptions,
  titleClassName,
}: GeneralFiltersSectionProps) => {
  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col">
      <p className={cn('px-2 pb-2 text-base font-medium', titleClassName)}>
        {title}
      </p>
      {groups.map((group) => {
        const groupToggles = toggledOptions[group.id] ?? {};
        const activeOptions = Object.entries(groupToggles)
          .filter(([_, isActive]) => isActive)
          .map(([optionId]) => {
            const option = group.options.find((opt) => opt.id === optionId);
            return option?.translatedDisplayName;
          })
          .filter((name): name is string => Boolean(name));

        return (
          <GeneralFilters.Item key={group.id} value={group.id}>
            <GeneralFilters.Trigger>
              {group.translatedDisplayName}
              <div className="ml-auto">
                <BadgeListWithOverflow items={activeOptions} />
              </div>
            </GeneralFilters.Trigger>

            <GeneralFilters.Content>
              <GeneralFiltersGroupOptions filterGroup={group} />
            </GeneralFilters.Content>
          </GeneralFilters.Item>
        );
      })}
    </div>
  );
};

// --------------------
// Accordion General Filters Content
// --------------------
export const GeneralFiltersContent = () => {
  const { common } = useAutoneTranslation();
  const { filterGroups, toggledOptions } = useGeneralFiltersContext();
  const filteredFilterGroups = useMemo(
    () => filterGroupsWithOptions(filterGroups),
    [filterGroups],
  );
  const hasFilterGroups = filteredFilterGroups.length > 0;
  const productFilterIds = useMemo(
    () => new Set<string>(PRODUCT_FILTER_ORDER),
    [],
  );
  const locationFilterIds = useMemo(
    () => new Set<string>(LOCATION_FILTER_ORDER),
    [],
  );
  const { productGroups, locationGroups } = useMemo(
    () =>
      partitionFilterGroups(
        filteredFilterGroups,
        productFilterIds,
        locationFilterIds,
      ),
    [filteredFilterGroups, productFilterIds, locationFilterIds],
  );

  if (!hasFilterGroups) return <GeneralFiltersNoOptions />;

  return (
    <GeneralFilters.Root>
      <div className="flex flex-col gap-4">
        <GeneralFiltersHeader />
        <GeneralFilters.Root>
          <GeneralFiltersSection
            title={common('filters.product')}
            groups={productGroups}
            toggledOptions={toggledOptions}
          />
          <GeneralFiltersSection
            title={common('filters.geographic')}
            groups={locationGroups}
            toggledOptions={toggledOptions}
            titleClassName="pt-4"
          />
        </GeneralFilters.Root>
      </div>
    </GeneralFilters.Root>
  );
};

// --------------------
// Accordion General Filters Header
// --------------------
export const GeneralFiltersHeader = () => {
  const { common } = useAutoneTranslation();

  return (
    <div className="flex items-center justify-between">
      <p className="text-lg font-semibold">
        {common('filters.general-filters-ui-next-header')}
      </p>
      <ClearAllButton id="general-filters-clear-all" />
    </div>
  );
};
